/**
 * OrderHeader — top strip of the Manifest page.
 *
 * Shows the contract context (title, buyer, value, award date) plus a
 * progress strip and the currently-active phase pill.
 */
import React from 'react';
import { Building2, Calendar, Hash, MapPin, RotateCcw } from 'lucide-react';
import { PHASES, STEPS, TARGET_TOTAL_DAYS, overallProgress, findCurrentStep, getPhase } from '../fixtures';

const formatCad = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M CAD`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K CAD`;
  return `$${n.toFixed(0)} CAD`;
};

const dayDiff = (iso) => {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

const OrderHeader = ({ order, onReset }) => {
  if (!order) return null;
  const days = dayDiff(order.awardDate);
  const { done, total, pct } = overallProgress(order.stepStates || {});
  const currentStep = findCurrentStep(order.stepStates || {});
  const currentPhase = getPhase(currentStep.phase);

  return (
    <div className="manifest-order-header">
      <div className="manifest-order-header-row">
        <div className="manifest-order-title-block">
          <div className="manifest-order-eyebrow">
            <span className="manifest-pill manifest-pill-phase">
              Phase {currentPhase.id} · {currentPhase.label}
            </span>
            <span className="manifest-day-counter">
              Day {days} of {TARGET_TOTAL_DAYS}
            </span>
          </div>
          <h1 className="manifest-order-title">{order.contractTitle}</h1>
          <div className="manifest-order-meta">
            <span><Building2 size={14} /> {order.buyer}</span>
            <span><Hash size={14} /> {order.solicitationNumber}</span>
            <span><MapPin size={14} /> {order.deliveryRegion}</span>
            <span><Calendar size={14} /> Awarded {formatDate(order.awardDate)}</span>
          </div>
        </div>

        <div className="manifest-order-stats">
          <div className="manifest-stat">
            <div className="manifest-stat-label">Contract value</div>
            <div className="manifest-stat-value">{formatCad(order.contractValueCad)}</div>
          </div>
          <div className="manifest-stat">
            <div className="manifest-stat-label">Quantity</div>
            <div className="manifest-stat-value">{order.quantity?.toLocaleString() || '—'}</div>
          </div>
          <div className="manifest-stat">
            <div className="manifest-stat-label">Steps complete</div>
            <div className="manifest-stat-value">{done} / {total}</div>
          </div>
          <button className="manifest-reset" onClick={onReset} title="Reset demo state">
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>

      {/* Progress strip — one dot per step, colored by phase */}
      <div className="manifest-progress-strip" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        {STEPS.map((s) => {
          const state = order.stepStates?.[s.id]?.status || 'pending';
          return (
            <div
              key={s.id}
              className={`manifest-progress-dot phase-${s.phase} status-${state}`}
              title={`Step ${s.id}: ${s.title} — ${state}`}
            />
          );
        })}
      </div>

      <div className="manifest-progress-legend">
        {PHASES.map((p) => (
          <span key={p.id} className={`manifest-legend-item phase-${p.id}`}>
            <span className="manifest-legend-swatch" /> {p.label}
          </span>
        ))}
        <span className="manifest-legend-pct">{pct}% complete</span>
      </div>
    </div>
  );
};

export default OrderHeader;
